<template>
  <div class="div-container">
    <validation-observer ref="refFormObserver">
      <b-form>
        <b-row>
          <b-col cols="12">
            <!-- Name -->
            <validation-provider
              #default="validationContext"
              name="name"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('group.name')"
                label-for="name"
              >
                <b-form-input
                  id="name"
                  v-model="itemData.name"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Users -->
            <validation-provider #default="validationContext">
              <b-form-group
                class="text-capitalize"
                :label="$tc('users.title', 2)"
                label-for="users"
                :state="getValidationState(validationContext)"
              >
                <UsersSelectPaginated
                  data-cy="users"
                  :value="usersDisplayValue"
                  multiple
                  :placeholder="$t('users.search_users')"
                  :extra-params="usersSelectExtraParams"
                  @input="onUsersInput"
                />

                <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
                >
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="6">
            <!-- check_alarms -->
            <validation-provider name="check_alarms">
              <b-form-checkbox
                id="check_alarms"
                v-model="itemData.check_alarms"
                name="check_alarms"
                value="1"
                unchecked-value="0"
                class="my-1 text-capitalize"
                switch
              >
                {{ $t("group.check_alarms") }}
              </b-form-checkbox>
            </validation-provider>
          </b-col>

          <b-col cols="6">
            <!-- color -->
            <validation-provider #default="validationContext" name="color">
              <b-form-group
                class="text-capitalize"
                :label="$t('alarm_level.color')"
                label-for="color"
              >
                <b-form-input
                  id="color"
                  v-model="itemData.frame_color"
                  :state="getValidationState(validationContext)"
                  trim
                  type="color"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BFormRadio,
} from "bootstrap-vue";
import { ref, watch, computed, nextTick } from "@vue/composition-api";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@core/comp-functions/forms/form-validation";
import UsersSelectPaginated from "@/components/UsersSelectPaginated.vue";
import { required, alphaNum, email, positive, required_if } from "@validations";

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    UsersSelectPaginated,
    BFormCheckbox,
    BFormRadio,
  },
  props: {
    propItemData: {
      type: Object,
      required: false,
    },
    initialUserLabels: {
      type: Array,
      default: () => [],
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
      required_if,
    };
  },

  setup(props, { emit }) {
    const isUpdatingFromProps = ref(false);
    const { refFormObserver, getValidationState } = formValidation();
    const itemData = ref(JSON.parse(JSON.stringify(props.propItemData)));
    const usersSelectExtraParams = { withGroup: false, withPermissions: false };
    const labelById = ref({});

    const getLabelFromOption = (it) => {
      if (it && it.email) return it.email;
      if (it && it.label) {
        const parts = String(it.label).split(" | ");
        return parts.length > 1 ? parts[parts.length - 1].trim() : it.label;
      }
      return null;
    };

    const usersDisplayValue = computed(() =>
      (itemData.value.users || []).map((id) => ({
        value: id,
        label: labelById.value[id] ?? String(id),
      }))
    );

    watch(
      () => props.initialUserLabels,
      (users) => {
        const map = (users || []).reduce((acc, u) => {
          if (u && u.id != null && (u.email != null || u.label != null))
            acc[u.id] = u.email ?? u.label;
          return acc;
        }, {});
        labelById.value = { ...labelById.value, ...map };
      },
      { deep: true, immediate: true }
    );

    const validateStep = () => refFormObserver.value.validate();

    const setupWatcher = (itemData, key) => {
      watch(
        () => itemData.value[key],
        (newValue, oldValue) => {
          if (newValue !== oldValue && !isUpdatingFromProps.value) {
            emit("update:itemData", { [key]: newValue });
          }
          nextTick(() => {
            isUpdatingFromProps.value = false;
          });
        }
      );
    };

    watch(
      () => props.propItemData,
      (newValue) => {
        isUpdatingFromProps.value = true;
        itemData.value = { ...newValue };
      },
      { deep: true }
    );

    setupWatcher(itemData, "name");
    setupWatcher(itemData, "id_country");
    setupWatcher(itemData, "check_alarms");
    setupWatcher(itemData, "frame_color");
    setupWatcher(itemData, "users");

    const onUsersInput = (value) => {
      const list = Array.isArray(value) ? value : value ? [value] : [];
      const seen = new Set();
      const ids = list
        .map((it) => it && (it.value !== undefined ? it.value : it.id))
        .filter((id) => id != null && !seen.has(id) && (seen.add(id), true));
      itemData.value.users = ids;
      list.forEach((it) => {
        const id = it && (it.value !== undefined ? it.value : it.id);
        const lbl = getLabelFromOption(it);
        if (id != null && lbl) labelById.value[id] = lbl;
      });
      labelById.value = { ...labelById.value };
    };

    return {
      itemData,
      usersDisplayValue,
      usersSelectExtraParams,
      refFormObserver,
      getValidationState,
      validateStep,
      onUsersInput,
      isUpdatingFromProps,
    };
  },
};
</script>

<style scoped>
.input-width {
  width: 80%; /* 80% father container */
  margin: 0 auto; /* Center in the father */
  display: block; /* block to marginauto */
}
</style>
